import { useState } from 'react';
import { Modal, message } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

/**
 * Custom hook to handle secure logout process
 * Validates agent status before logging out to prevent dropped calls or data loss.
 */
const useLogout = () => {
    const { logout } = useAuth();
    const [isLoading, setIsLoading] = useState(false);

    const handleLogout = async () => {
        setIsLoading(true);
        try {
            // 1. Check current agent status
            const snapshot = await api.get('/pbx/agents/state_snapshot.php');

            if (snapshot.data.status === 'success') {
                const currentStatus = snapshot.data.data.status;
                const activeStates = ['incall', 'wrapup', 'hold']; // Critical states

                // 2. If in a critical state, BLOCK/WARN with stronger message
                if (activeStates.includes(currentStatus)) {
                    Modal.confirm({
                        title: '⚠️ No se puede cerrar sesión',
                        icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
                        content: (
                            <div>
                                <p>Tiene una actividad pendiente (Llamada o Cierre). </p>
                                <p>Estado actual: <strong>{currentStatus.toUpperCase()}</strong></p>
                                <p>Debe terminar su actividad actual antes de salir.</p>
                            </div>
                        ),
                        okText: 'Entendido',
                        cancelButtonProps: { style: { display: 'none' } }, // Hide cancel, force ack
                        onOk: () => setIsLoading(false),
                    });
                    return;
                }

                // 3. If in standard working state (online/paused), Confirm Intent
                if (currentStatus !== 'offline') {
                    Modal.confirm({
                        title: '¿Está seguro de cerrar sesión?',
                        icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
                        okText: 'Sí, cerrar sesión',
                        cancelText: 'Cancelar',
                        onOk: async () => {
                            await executeLogout();
                        },
                        onCancel: () => setIsLoading(false),
                    });
                    return;
                }
            }

            // 4. Fallback / Already Offline -> Just logout
            await executeLogout();

        } catch (error) {
            console.error("Error checking status before logout", error);
            // On error (e.g. network fail), ask for confirmation to force exit
            Modal.confirm({
                title: 'Error de conexión',
                content: 'No se pudo verificar su estado. ¿Desea cerrar sesión forzosamente?',
                okText: 'Sí, salir',
                onOk: async () => await executeLogout(),
                onCancel: () => setIsLoading(false),
            });
        }
    };

    const executeLogout = async () => {
        try {
            await logout(); // This is now async and waits for backend
        } catch (e) {
            console.error("Logout failed", e);
            message.error("Error al cerrar sesión");
        }
        // Redirect is handled by AuthContext state change or manual nav if needed
        window.location.href = '/login';
    };

    return {
        handleLogout,
        isLoading
    };
};

export default useLogout;
